import {
  BattlefieldDefDTO,
  BattlefieldDefSchema,
  CombatActionResponseDTO,
  CombatActionResponseSchema,
  CombatCheatEndRequestDTO,
  CombatEndTurnRequestDTO,
  CombatMoveRequestDTO,
  CombatPosition,
  CombatResolveResponseDTO,
  CombatResolveResponseSchema,
  CombatUseAbilityRequestDTO,
  CombatUseItemRequestDTO,
  GameStateDTO,
  GetCombatStateResponseDTO,
  GetCombatStateResponseSchema,
} from '@hexcrawl/shared-utils';
import { requestJson } from '@/ui/services/api';

export const combatService = {
  getBattlefieldDef(id: string): Promise<BattlefieldDefDTO> {
    return requestJson<BattlefieldDefDTO>({
      url: `/game/battlefield?id=${encodeURIComponent(id)}`,
      method: 'GET',
      schema: BattlefieldDefSchema,
    });
  },

  getCombatState(id: string): Promise<GetCombatStateResponseDTO> {
    const url = `/game/combat/state?id=${encodeURIComponent(id)}`;
    return requestJson<GetCombatStateResponseDTO>({
      url,
      method: 'GET',
      schema: GetCombatStateResponseSchema,
    });
  },

  move(combatId: string, moveTo: CombatPosition): Promise<CombatActionResponseDTO> {
    const req: CombatMoveRequestDTO = { id: combatId, moveTo };
    return requestJson<CombatActionResponseDTO>({
      url: '/game/combat/move',
      method: 'POST',
      data: req,
      schema: CombatActionResponseSchema,
    });
  },

  useAbility(combatId: string, abilityId: string, targetEntityId?: string, targetPosition?: CombatPosition): Promise<CombatActionResponseDTO> {
    const req: CombatUseAbilityRequestDTO = { id: combatId, abilityId, targetEntityId, targetPosition };
    return requestJson<CombatActionResponseDTO>({
      url: '/game/combat/use-ability',
      method: 'POST',
      data: req,
      schema: CombatActionResponseSchema,
    });
  },

  useItem(combatId: string, instanceId: string, targetEntityId?: string, targetPosition?: CombatPosition): Promise<CombatActionResponseDTO> {
    const req: CombatUseItemRequestDTO = { id: combatId, instanceId, targetEntityId, targetPosition };
    return requestJson<CombatActionResponseDTO>({
      url: '/game/combat/use-item',
      method: 'POST',
      data: req,
      schema: CombatActionResponseSchema,
    });
  },

  endTurn(combatId: string): Promise<CombatActionResponseDTO> {
    const req: CombatEndTurnRequestDTO = { id: combatId };
    return requestJson<CombatActionResponseDTO>({
      url: '/game/combat/end-turn',
      method: 'POST',
      data: req,
      schema: CombatActionResponseSchema,
    });
  },

  cheatEnd(combatId: string, outcome: 'win' | 'lose'): Promise<CombatActionResponseDTO> {
    const req: CombatCheatEndRequestDTO = { id: combatId, outcome };
    return requestJson<CombatActionResponseDTO>({
      url: '/game/combat/cheat-end',
      method: 'POST',
      data: req,
      schema: CombatActionResponseSchema,
    });
  },

  resolve(combatId: string, action?: 'revive'): Promise<CombatResolveResponseDTO> {
    return requestJson<CombatResolveResponseDTO>({
      url: '/game/combat/resolve',
      method: 'POST',
      data: { combatId, action },
      schema: CombatResolveResponseSchema,
    });
  },

  async quit(combatId: string): Promise<void> {
    await requestJson({
      url: '/game/combat/resolve',
      method: 'POST',
      data: { combatId, action: 'quit' },
    });
  },
};

/**
 * Fetches the BattlefieldDef for the active combat, then starts CombatScene
 * with both game state and def as init data. Must be awaited before the scene
 * starts so that preload() has the def available.
 */
export async function launchCombatScene(gameState: GameStateDTO): Promise<void> {
  const battlefieldId = gameState.activeCombat?.battlefieldId;
  if (!battlefieldId) {
    throw new Error('[launchCombatScene] activeCombat has no battlefieldId');
  }
  const battlefieldDef = await combatService.getBattlefieldDef(battlefieldId);
  const game = window.phaserGame;
  if (game?.scene) {
    game.scene.start('CombatScene', { gameState, battlefieldDef });
  }
}
